// src/api/bchArbitrage.js
import { apiGet, apiPost } from "./http";

/**
 * =========================
 * BCH 套利产品配置
 * =========================
 */

/**
 * GET /app/bch/arbitrage/config
 *
 * 获取当前 BCH 套利产品配置：
 * - 标准订单计划 ID
 * - 体验订单计划 ID
 * - 标准订单最低金额
 * - AI 模型费率
 * - 当前用户日收益率
 * - 体验订单金额
 * - 体验订单周期
 * - 体验订单固定收益
 * - 今日体验订单名额
 * - 当前地址是否购买过体验订单
 * - 本金赎回周期与手续费
 */
export function fetchBchArbitrageConfig(config = {}) {
  return apiGet("/app/bch/arbitrage/config", {}, config);
}

/**
 * =========================
 * BCH 用户订单统计
 * =========================
 */

/**
 * GET /app/bch/arbitrage/orders/stat
 *
 * 获取当前登录用户的订单统计：
 * - 总订单数量
 * - 标准订单数量
 * - 体验订单数量
 * - 运行中订单数量
 * - 已完成订单数量
 * - 累计委托金额
 * - 当前计息本金
 * - 释放中本金
 * - 已赎回本金
 * - 累计收益
 * - 可提现收益
 */
export function fetchBchOrderStat(config = {}) {
  return apiGet("/app/bch/arbitrage/orders/stat", {}, config);
}

/**
 * =========================
 * BCH 用户订单分页
 * =========================
 */

/**
 * GET /app/bch/arbitrage/orders/page
 *
 * 当前登录用户订单分页查询。
 *
 * @param {Object} params
 *  - pageNum: 页码，从 1 开始
 *  - pageSize: 每页数量
 *  - orderType: 订单类型，可选
 *      1 = 标准套利订单
 *      2 = 七天体验订单
 *  - status: 订单状态，可选
 *      0 = 待链上确认
 *      1 = 生效计息中
 *      2 = 已停止计息 / 到期待提取
 *      3 = 已完成
 *      4 = 已取消
 *      5 = 链上处理失败
 *  - redeemStatus: 本金赎回状态，可选
 *      0 = 未赎回
 *      1 = 部分赎回中
 *      2 = 全部本金已申请赎回
 *      3 = 全部本金已完成赎回
 */
export function fetchBchOrderPage(params = {}, config = {}) {
  const {
    pageNum = 1,
    pageSize = 10,
    orderType,
    status,
    redeemStatus,
  } = params || {};

  const query = {
    pageNum,
    pageSize,
  };

  if (orderType !== undefined && orderType !== null && orderType !== "") {
    query.orderType = orderType;
  }

  if (status !== undefined && status !== null && status !== "") {
    query.status = status;
  }

  if (
    redeemStatus !== undefined &&
    redeemStatus !== null &&
    redeemStatus !== ""
  ) {
    query.redeemStatus = redeemStatus;
  }

  return apiGet("/app/bch/arbitrage/orders/page", query, config);
}

/**
 * =========================
 * BCH 用户订单详情
 * =========================
 */

/**
 * GET /app/bch/arbitrage/orders/{orderNo}
 *
 * 根据业务订单号查询当前登录用户的订单详情。
 *
 * @param {String} orderNo 业务订单号
 * @param {Object} config 请求配置
 */
export function fetchBchOrderDetail(orderNo, config = {}) {
  if (!orderNo) {
    return Promise.reject(new Error("订单号不能为空"));
  }

  return apiGet(
    `/app/bch/arbitrage/orders/${encodeURIComponent(orderNo)}`,
    {},
    config
  );
}

/**
 * =========================
 * 标准订单本金赎回
 * =========================
 */

/**
 * POST /app/bch/arbitrage/orders/redeem
 *
 * 标准套利订单申请本金赎回。
 *
 * @param {Object} data
 *  - orderNo: 业务订单号
 *  - releaseDays: 释放周期，只支持 7 或 30
 *
 * 业务说明：
 * - 每次按照订单的 redeemUnitRate 赎回
 * - 当前规则默认每次赎回原始套利本金的 20%
 * - 发起赎回后，该部分本金立即停止产生收益
 * - 7 天释放收取配置的手续费
 * - 30 天释放默认无手续费
 */
export function applyBchPrincipalRedeem(data = {}, config = {}) {
  const { orderNo, releaseDays } = data || {};

  if (!orderNo) {
    return Promise.reject(new Error("订单号不能为空"));
  }

  const days = Number(releaseDays);

  if (days !== 7 && days !== 30) {
    return Promise.reject(new Error("本金释放周期只能选择 7 天或 30 天"));
  }

  return apiPost(
    "/app/bch/arbitrage/orders/redeem",
    {
      orderNo,
      releaseDays: days,
    },
    config
  );
}

/**
 * =========================
 * 体验订单到期提取
 * =========================
 */

/**
 * POST /app/bch/arbitrage/orders/trial/claim
 *
 * 七天体验订单到期后申请连本带利提取。
 *
 * @param {Object|String} params
 *
 * 支持两种调用方式：
 *
 * claimBchTrialOrder({
 *   orderNo: "BCHT202606..."
 * })
 *
 * 或：
 *
 * claimBchTrialOrder("BCHT202606...")
 */
export function claimBchTrialOrder(params = {}, config = {}) {
  const orderNo =
    typeof params === "string"
      ? params
      : params && params.orderNo;

  if (!orderNo) {
    return Promise.reject(new Error("订单号不能为空"));
  }

  return apiPost(
    "/app/bch/arbitrage/orders/trial/claim",
    {
      orderNo,
    },
    config
  );
}

/**
 * =========================
 * 兼容名称导出
 * =========================
 *
 * 下面这些别名方便订单管理页面使用。
 * 不需要时也可以删除。
 */

export const fetchBchConfig = fetchBchArbitrageConfig;
export const fetchBchStat = fetchBchOrderStat;
export const fetchBchOrders = fetchBchOrderPage;
export const fetchBchDetail = fetchBchOrderDetail;